import { CompanyType } from '@treadinc/horizon-api-spec';
import { startCase, uniqBy } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';

import { AppliedFiltersChipsCollectedData } from '~components/Filters/AppliedFiltersChips';
import { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import { DriverSelectedFilter } from '~constants/enums';

export type Filters = {
  senderCompanyTypes?: CompanyType[];
  labels?: string[];
};

type DriverFilterKey = Extract<keyof Filters, 'senderCompanyTypes' | 'labels'>;

type AppliedFiltersState = Record<
  DriverSelectedFilter,
  {
    driverFilterKey: DriverFilterKey;
    search: string;
  }
>;

export const driverFilterKeyBySelectedFilter: Record<
  DriverSelectedFilter,
  DriverFilterKey
> = {
  [DriverSelectedFilter.SENDER_COMPANY_TYPES]: 'senderCompanyTypes',
  [DriverSelectedFilter.LABELS]: 'labels',
};

type FiltersAvailableOptionsState = Record<DriverSelectedFilter, FilterMenuOption[]>;

const getAppliedFiltersInitialState = () => {
  const initialState = Object.entries(driverFilterKeyBySelectedFilter).reduce(
    (obj, [filterKey, storeKey]) => {
      const key = filterKey as DriverSelectedFilter;

      obj[key] = {
        driverFilterKey: storeKey,
        search: '',
      };

      return obj;
    },
    {} as AppliedFiltersState,
  );

  return initialState;
};

const getAppliedFiltersChipsCollectedDataInitialState = (
  availableOptions: FiltersAvailableOptionsState,
) => {
  const collectedData = Object.entries(availableOptions).reduce((obj, [key, options]) => {
    obj[key as DriverSelectedFilter] = options;

    return obj;
  }, {} as AppliedFiltersChipsCollectedData);

  return collectedData;
};

const AVAILABLE_OPTIONS_STORAGE_KEY = 'driver_availableOptions';
type AppliedFiltersStoredAvailableOptions = Partial<
  Record<DriverSelectedFilter, FilterMenuOption[]>
>;

const getAvailableOptionsInitialState = () => {
  const stored = localStorage.getItem(AVAILABLE_OPTIONS_STORAGE_KEY);
  const parsed = (
    stored ? JSON.parse(stored) : {}
  ) as AppliedFiltersStoredAvailableOptions;

  const initialState = {
    ...Object.values(DriverSelectedFilter).reduce((obj, filter) => {
      obj[filter] = parsed[filter] ?? [];

      return obj;
    }, {} as FiltersAvailableOptionsState),
  };

  initialState.senderCompanyTypes = Object.values(CompanyType).map((type) => ({
    label: startCase(type),
    value: `${type}`,
  }));

  return initialState;
};

export default function useDriverFilters() {
  const [appliedFilters, setAppliedFilters] = useState(getAppliedFiltersInitialState());
  const [availableOptions, setAvailableOptions] = useState(
    getAvailableOptionsInitialState(),
  );

  const [appliedFilterChipsCollectedData, setAppliedFilterChipsCollectedData] = useState(
    getAppliedFiltersChipsCollectedDataInitialState(availableOptions),
  );

  const updateAppliedFilterChipsCollectedData = useCallback(
    (filter: string, data: FilterMenuOption[]) => {
      const isValidFilter =
        driverFilterKeyBySelectedFilter[filter as DriverSelectedFilter];

      if (isValidFilter) {
        setAppliedFilterChipsCollectedData((state) => ({
          ...state,
          [filter]: uniqBy([...(state[filter] ?? []), ...data], (i) => i.value),
        }));
      }
    },
    [],
  );

  useEffect(() => {
    const options = Object.entries(availableOptions).reduce((obj, [key, options]) => {
      obj[key as DriverSelectedFilter] = options;

      return obj;
    }, {} as AppliedFiltersStoredAvailableOptions);

    localStorage.setItem(AVAILABLE_OPTIONS_STORAGE_KEY, JSON.stringify(options));
  }, [JSON.stringify(availableOptions)]);

  return {
    availableOptions,
    updateAvailableOptions: setAvailableOptions,
    appliedFilters,
    updateAppliedFilters: setAppliedFilters,
    appliedFilterChipsCollectedData,
    updateAppliedFilterChipsCollectedData,
  };
}
